import { ref, Ref } from 'vue';

/**
 * Composable hook that wraps a fetch function to prevent duplicate execution.
 * Useful when multiple actions (e.g., confirm/approve/complete) might trigger
 * refetch simultaneously, ensuring only one refetch executes at a time.
 *
 * @param fetchFn - The fetch function to wrap
 * @returns An object containing:
 *   - safeRefetch: A function that safely executes the fetch, preventing duplicates
 *   - isRefetching: A reactive ref indicating if a refetch is currently in progress
 *
 * @example
 * ```typescript
 * const { safeRefetch } = useSafeRefetch(fetchBienBanGiaiTheItems);
 * // Use safeRefetch as the onSuccess callback
 * ```
 */
export const useSafeRefetch = (
  fetchFn: () => Promise<void>
): {
  safeRefetch: () => Promise<void>;
  isRefetching: Ref<boolean>;
} => {
  const isRefetching = ref(false);

  const safeRefetch = async () => {
    // Prevent duplicate execution if already refetching
    if (isRefetching.value) {
      return;
    }

    try {
      isRefetching.value = true;
      await fetchFn();
    } finally {
      // Always reset the flag, even if fetchFn throws an error
      isRefetching.value = false;
    }
  };

  return {
    safeRefetch,
    isRefetching,
  };
};
